/**
 * Answers waiting to reach the server.
 *
 * A child answers a question every ten seconds on a device that is often on a
 * train or at the far end of a house. Posting each answer the moment it happens
 * gives twenty chances per lesson to lose one; holding them all to the end gives
 * one chance to lose twenty. So answers are buffered and flushed, and a flush
 * that fails keeps everything it was carrying.
 *
 * What makes that safe is the server's side of the bargain: `attemptId` is
 * generated here, and `attempt.record` is idempotent by it
 * ([Data model](../../../docs/DATA-MODEL.md)) — right down to the FSRS
 * schedules and mastery folded forward from it
 * ([D-059](../../../docs/DECISIONS.md#d-059)). So a batch may be re-sent freely,
 * which is what lets this retry the *same* batch after a token expires
 * mid-lesson rather than having to work out what got through.
 *
 * Nothing here touches React or the network. It is a list and two rules.
 */

/** One answer, in the shape the wire takes: `at` is a string because JSON is. */
export interface WireAttempt {
  readonly attemptId: string;
  readonly exerciseId: string;
  readonly sessionId: string;
  readonly outcome: 'correct' | 'partial' | 'incorrect' | 'skipped';
  readonly score: number;
  readonly latencyMs: number;
  readonly hintsUsed: number;
  readonly phonemeScores: Readonly<Record<string, number>> | null;
  readonly at: string;
}

/**
 * The most the server accepts in one call.
 *
 * Mirrors `MAX_BATCH` in `apps/server/src/attempts.ts`. A lesson is fifteen
 * minutes and twenty-odd exercises, so this is never reached in practice — it
 * is here so that a lesson which somehow does reach it sends two batches
 * instead of having the tail silently truncated by the server.
 */
export const MAX_BATCH = 60;

export type Send = (batch: readonly WireAttempt[]) => Promise<unknown>;

export class Outbox {
  private queue: WireAttempt[] = [];
  private inFlight: Promise<boolean> | null = null;

  add(attempt: WireAttempt): void {
    this.queue.push(attempt);
  }

  /** What has not been acknowledged yet. Oldest first. */
  pending(): readonly WireAttempt[] {
    return this.queue;
  }

  /**
   * Send what is waiting, and report whether it got through.
   *
   * Two flushes never overlap: the second waits for the first and then sends
   * whatever is left. Overlapping them would put the same answers on the wire
   * twice — harmless, because the server is idempotent, but it would also mean
   * a failed flush could clear answers a concurrent one was still carrying.
   */
  async flush(send: Send): Promise<boolean> {
    while (this.inFlight !== null) await this.inFlight;
    if (this.queue.length === 0) return true;

    const batch = this.queue.slice(0, MAX_BATCH);
    const attempt = this.send(send, batch);
    this.inFlight = attempt;
    try {
      return await attempt;
    } finally {
      this.inFlight = null;
    }
  }

  private async send(send: Send, batch: readonly WireAttempt[]): Promise<boolean> {
    try {
      await send(batch);
    } catch {
      // Kept, in order, for the next flush. The caller decides whether that is
      // now (a token to refresh) or at the end of the lesson.
      return false;
    }
    const sent = new Set(batch.map((a) => a.attemptId));
    this.queue = this.queue.filter((a) => !sent.has(a.attemptId));
    return true;
  }
}
